import {
  deliveriesResponseSchema,
  deliverySchema,
  type Delivery,
  type DeliveriesResponse,
  type DeliveryStatus,
} from '../types';
import {
  ApiClientError, buildUrl 
} from '@/errors';
import {
  authFetch, API_BASE_URL 
} from '@/lib/api-client';
import { z } from 'zod';

export interface DeliveryFilters {
  readonly status?: DeliveryStatus;
  readonly supplierId?: string;
}

export async function getDeliveries(siteId: string, filters?: DeliveryFilters): Promise<DeliveriesResponse> {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.supplierId) params.set('supplierId', filters.supplierId);

  const url = buildUrl(API_BASE_URL, `/v1/sites/${siteId}/deliveries`, params);
  const response = await authFetch(url);
  
  if (!response.ok) {
    throw ApiClientError.fromResponse(response, 'Failed to fetch deliveries');
  }

  return deliveriesResponseSchema.parse(await response.json());
}

const deliveryByPoResponseSchema = z.object({data: deliverySchema,});

export async function getDeliveryByPoNumber(poNumber: string): Promise<Delivery> {
  const url = buildUrl(API_BASE_URL, `/v1/deliveries/by-po/${encodeURIComponent(poNumber)}`);
  const response = await authFetch(url);
  
  if (!response.ok) {
    throw ApiClientError.fromResponse(response, `Failed to fetch delivery for PO ${poNumber}`);
  }

  const parsed = deliveryByPoResponseSchema.parse(await response.json());
  return parsed.data;
}

export interface UpdateDeliveryLinesRequest {
  lines: Array<{
    lineId: string;
    receivedQuantity: number;
    qualityStatus?: 'pending' | 'passed' | 'failed' | 'hold';
    temperature?: number;
    lotNumber?: string;
    expiryDate?: string;
    notes?: string;
  }>;
}

export async function updateDeliveryLines(
  deliveryId: string,
  request: UpdateDeliveryLinesRequest
): Promise<Delivery> {
  const url = buildUrl(API_BASE_URL, `/v1/deliveries/${deliveryId}/lines`);
  const response = await authFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  
  if (!response.ok) {
    throw ApiClientError.fromResponse(response, 'Failed to update delivery lines');
  }

  const parsed = deliveryByPoResponseSchema.parse(await response.json());
  return parsed.data;
}

export interface CompleteReceivingRequest {notes?: string;}

export async function completeReceiving(
  deliveryId: string,
  request?: CompleteReceivingRequest
): Promise<Delivery> {
  const url = buildUrl(API_BASE_URL, `/v1/deliveries/${deliveryId}/complete`);
  const response = await authFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request ?? {}),
  });
  
  if (!response.ok) {
    throw ApiClientError.fromResponse(response, 'Failed to complete receiving');
  }

  const parsed = deliveryByPoResponseSchema.parse(await response.json());
  return parsed.data;
}

export async function startReceiving(deliveryId: string): Promise<Delivery> {
  const url = buildUrl(API_BASE_URL, `/v1/deliveries/${deliveryId}/start-receiving`);
  const response = await authFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  
  if (!response.ok) {
    throw await ApiClientError.fromResponseWithBody(response, 'Failed to start receiving');
  }

  const parsed = deliveryByPoResponseSchema.parse(await response.json());
  return parsed.data;
}
